/** Request categories (tree). */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type CategoryNodeResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Dialog as Modal } from "@primereact/ui/dialog";
import { Select } from "@primereact/ui/select";
import { ToggleSwitch as Toggle } from "@primereact/ui/toggleswitch";
import { apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { MutationError, Section } from "../shared.js";

export function CategoriesForm() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [activeOnly, setActiveOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [translationNames, setTranslationNames] = useState({
    de: "",
    fr: "",
    es: "",
  });
  const [customTranslations, setCustomTranslations] = useState(false);
  const [parentId, setParentId] = useState("");
  const displayLocale = i18n.resolvedLanguage?.split("-")[0];
  const localizedName = (category: CategoryNodeResponse): string => {
    if (
      displayLocale === "de" ||
      displayLocale === "fr" ||
      displayLocale === "es"
    ) {
      return category.translations[displayLocale] ?? category.name;
    }
    return category.name;
  };
  const query = useQuery({
    queryKey: ["categories", "settings"],
    queryFn: () =>
      apiGet<{
        data: CategoryNodeResponse[];
      }>("/settings/categories", {
        query: {
          includeInactive: true,
        },
      }).then((response) => response.data),
  });
  const resetCreate = () => {
    setName("");
    setTranslationNames({
      de: "",
      fr: "",
      es: "",
    });
    setCustomTranslations(false);
    setParentId("");
  };
  const openCreate = (defaultParentId = "") => {
    resetCreate();
    setParentId(defaultParentId);
    setCreateOpen(true);
  };
  const create = useMutation({
    mutationFn: () => {
      const trimmedName = name.trim();
      const translations = Object.fromEntries(
        (["de", "fr", "es"] as const).map((locale) => [
          locale,
          {
            name:
              (customTranslations
                ? translationNames[locale].trim()
                : trimmedName) || trimmedName,
          },
        ]),
      );
      return apiPost("/settings/categories", {
        name: trimmedName,
        parentId: parentId || null,
        translations,
      });
    },
    onSuccess: async () => {
      setCreateOpen(false);
      resetCreate();
      await queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
  });
  const update = useMutation({
    mutationFn: ({
      category,
      data,
    }: {
      category: CategoryNodeResponse;
      data: Record<string, unknown>;
    }) => apiPatch(`/settings/categories/${category.id}`, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
  });
  const reorder = useMutation({
    mutationFn: async ({
      category,
      siblings,
      direction,
    }: {
      category: CategoryNodeResponse;
      siblings: CategoryNodeResponse[];
      direction: -1 | 1;
    }) => {
      const index = siblings.findIndex((item) => item.id === category.id);
      const adjacent = siblings[index + direction];
      if (!adjacent) return;
      await Promise.all([
        apiPatch(`/settings/categories/${category.id}`, {
          sortOrder: adjacent.sortOrder,
        }),
        apiPatch(`/settings/categories/${adjacent.id}`, {
          sortOrder: category.sortOrder,
        }),
      ]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
  });
  const categories =
    query.data
      ?.filter((category) => !activeOnly || category.active)
      .map((category) => ({
        ...category,
        children: category.children.filter(
          (child) => !activeOnly || child.active,
        ),
      })) ?? [];
  const renameCategory = (category: CategoryNodeResponse) => {
    const nextName = window
      .prompt(
        t("settings.categoryName", {
          defaultValue: "Category name",
        }),
        localizedName(category),
      )
      ?.trim();
    if (!nextName || nextName === localizedName(category)) return;
    update.mutate({
      category,
      data: {
        name: nextName,
        ...(displayLocale === "de" ||
        displayLocale === "fr" ||
        displayLocale === "es"
          ? {
              translations: {
                [displayLocale]: {
                  name: nextName,
                },
              },
            }
          : {}),
      },
    });
  };
  const categoryActions = (
    category: CategoryNodeResponse,
    siblings: CategoryNodeResponse[],
    allowChildren: boolean,
  ) => {
    const index = siblings.findIndex((item) => item.id === category.id);
    return (
      <div className="flex flex-wrap items-center justify-end gap-1">
        <Button
          size="small"
          variant="text"
          disabled={index <= 0 || reorder.isPending}
          aria-label={t("common.moveUp", {
            defaultValue: "Move up",
          })}
          onClick={() =>
            reorder.mutate({
              category,
              siblings,
              direction: -1,
            })
          }
        >
          ↑
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={
            index < 0 || index >= siblings.length - 1 || reorder.isPending
          }
          aria-label={t("common.moveDown", {
            defaultValue: "Move down",
          })}
          onClick={() =>
            reorder.mutate({
              category,
              siblings,
              direction: 1,
            })
          }
        >
          ↓
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => renameCategory(category)}
        >
          {t("common.rename", {
            defaultValue: "Rename",
          })}
        </Button>
        {allowChildren ? (
          <Button
            size="small"
            variant="text"
            onClick={() => openCreate(category.id)}
          >
            {t("settings.addSubcategory", {
              defaultValue: "Add subcategory",
            })}
          </Button>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          severity="secondary"
          loading={update.isPending || undefined}
          onClick={() =>
            update.mutate({
              category,
              data: {
                active: !category.active,
              },
            })
          }
        >
          {category.active
            ? t("common.deactivate", {
                defaultValue: "Deactivate",
              })
            : t("common.activate", {
                defaultValue: "Activate",
              })}
        </Button>
      </div>
    );
  };
  return (
    <Section
      title={t("settings.categories", {
        defaultValue: "Categories",
      })}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <label
            className="flex items-center gap-3 text-sm font-medium"
            htmlFor="categories-active-only"
          >
            <Toggle.Root
              inputId="categories-active-only"
              checked={activeOnly}
              onCheckedChange={(event: { checked: boolean }) =>
                setActiveOnly(event.checked)
              }
            >
              <Toggle.Control>
                <Toggle.Handle></Toggle.Handle>
              </Toggle.Control>
            </Toggle.Root>
            <span>
              {t("settings.activeOnly", {
                defaultValue: "Active only",
              })}
            </span>
          </label>
        </div>
        <Button onClick={() => openCreate()}>
          {t("settings.addCategory", {
            defaultValue: "Add category",
          })}
        </Button>
      </div>
      <div className="space-y-3">
        {categories.map((category) => (
          <div
            key={category.id}
            className="rounded-lg border border-app-border p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong>{localizedName(category)}</strong>
              {categoryActions(category, query.data ?? [], true)}
            </div>
            {category.children.map((child) => (
              <div
                key={child.id}
                className="ml-5 mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-app-border pt-2 text-sm"
              >
                <span>{localizedName(child)}</span>
                {categoryActions(child, category.children, false)}
              </div>
            ))}
          </div>
        ))}
      </div>
      <Modal.Root
        open={createOpen}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value &&
          (() => {
            if (!create.isPending) setCreateOpen(false);
          })()
        }
      >
        <Modal.Portal>
          <Modal.Backdrop></Modal.Backdrop>
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {t("settings.addCategory", {
                    defaultValue: "Add category",
                  })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times"></Modal.Close>
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <form
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    create.mutate();
                  }}
                >
                  <Field
                    label={t("settings.categoryName", {
                      defaultValue: "Category name",
                    })}
                    required
                  >
                    <Input
                      required
                      value={name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setName(event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t("settings.parent", {
                      defaultValue: "Parent category",
                    })}
                  >
                    <Select.Root
                      value={parentId}
                      onValueChange={(event: { value: unknown }) =>
                        setParentId(event.value as string)
                      }
                      fluid
                      options={[
                        {
                          value: "",
                          label: t("settings.noParent", {
                            defaultValue: "No parent",
                          }),
                        },
                        ...(query.data
                          ?.filter((category) => !activeOnly || category.active)
                          .map((category) => ({
                            value: category.id,
                            label: localizedName(category),
                          })) ?? []),
                      ]}
                      optionLabel="label"
                      optionValue="value"
                    >
                      <Select.Trigger type="button">
                        <Select.Value />
                        <Select.Indicator className="pi pi-chevron-down" />
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Positioner>
                          <Select.Popup>
                            <Select.List />
                          </Select.Popup>
                        </Select.Positioner>
                      </Select.Portal>
                    </Select.Root>
                  </Field>
                  <div>
                    <label
                      className="flex items-center gap-3 text-sm font-medium"
                      htmlFor="category-custom-translations"
                    >
                      <Toggle.Root
                        inputId="category-custom-translations"
                        checked={customTranslations}
                        onCheckedChange={(event: { checked: boolean }) =>
                          ((checked) => {
                            setCustomTranslations(checked);
                            if (checked) {
                              setTranslationNames((current) => ({
                                de: current.de || name,
                                fr: current.fr || name,
                                es: current.es || name,
                              }));
                            }
                          })(event.checked)
                        }
                      >
                        <Toggle.Control>
                          <Toggle.Handle></Toggle.Handle>
                        </Toggle.Control>
                      </Toggle.Root>
                      <span>
                        {t("settings.customTranslations", {
                          defaultValue: "Custom translations",
                        })}
                      </span>
                    </label>
                  </div>
                  {customTranslations ? (
                    <div className="grid gap-3 sm:grid-cols-3">
                      {(["de", "fr", "es"] as const).map((locale) => (
                        <Field key={locale} label={t(`locale.${locale}`)}>
                          <Input
                            value={translationNames[locale]}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              setTranslationNames((current) => ({
                                ...current,
                                [locale]: event.target.value,
                              }))
                            }
                          />
                        </Field>
                      ))}
                    </div>
                  ) : null}
                  <MutationError error={create.error} />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outlined"
                      severity="secondary"
                      disabled={create.isPending}
                      onClick={() => setCreateOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      loading={create.isPending || undefined}
                    >
                      {t("common.add")}
                    </Button>
                  </div>
                </form>
              </Modal.Content>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
      <MutationError error={update.error || reorder.error} />
    </Section>
  );
}
